/**
 * Somebody's face, or initials on a gradient derived from their handle.
 *
 * The gradient is the mockup's treatment and is still what most accounts
 * show: it is deterministic in the handle, so a person is the same two
 * colours everywhere and never flickers between renders.
 *
 * `avatarUrl` is the uploaded picture (POLISH-003) and wins when it is set.
 * It falls back to the initials on `onError` for the same reason `Artwork`
 * does: a file deleted underneath us, or a purge that raced a render, should
 * degrade to the placeholder rather than to a broken-image icon.
 *
 * Decorative in every place it is used — a name is always beside it, or the
 * link around it carries its own label — so it stays `aria-hidden`. A second
 * announcement of the same handle is noise in a screen reader, not help.
 */
import { useState } from 'react'

function hueFor(seed) {
  let hash = 0
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) % 360
  return hash
}

// Whole graphemes, not UTF-16 units (Q20): `part[0]` of an emoji word is half
// a surrogate pair, which draws as a replacement box. A flag is two code
// points and one grapheme, so `Array.from` is only the fallback.
const graphemes =
  typeof Intl !== 'undefined' && Intl.Segmenter
    ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
    : null

function firstGrapheme(word) {
  if (graphemes) return graphemes.segment(word)[Symbol.iterator]().next().value?.segment ?? ''
  return Array.from(word)[0] ?? ''
}

export default function Avatar({ name = '', size = 36, avatarUrl = null }) {
  const [failed, setFailed] = useState(false)
  const seed = name || '?'
  const hue = hueFor(seed)
  const initials = seed
    .split(/[\s_]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => firstGrapheme(part).toUpperCase())
    .join('')

  if (avatarUrl && !failed) {
    return (
      <img
        className="avatar"
        src={avatarUrl}
        width={size}
        height={size}
        alt=""
        aria-hidden="true"
        decoding="async"
        onError={() => setFailed(true)}
        style={{ width: size, height: size, objectFit: 'cover' }}
      />
    )
  }

  return (
    <span
      className="avatar"
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.4),
        background: `linear-gradient(135deg, hsl(${hue} 62% 56%), hsl(${(hue + 40) % 360} 66% 46%))`,
      }}
    >
      {initials || '?'}
    </span>
  )
}
